// The per-area status vocabulary and who may report what — one module for the
// web, mobile and the server, so the three can't drift (area review stage
// spec, rulings 2, 3 and 6). Not under `client/`: the backend reads it too.
//
// The vocabulary is TRUSSTOR'S (2026-09-17 task-commit design). It computes
// `inProgress` / `delayed` / `delayedOverdue` / `overdue` from tag work time;
// a person reports an area DONE, or READY FOR REVIEW, and can take either
// back (`planned` — Trusstor then recomputes it). `unplanned` is what an
// uncommitted task's areas read as: nothing has been promised there yet.

export const AREA_STATUSES = [
  'unplanned',
  'planned',
  'inProgress',
  'delayed',
  'delayedOverdue',
  'overdue',
  'readyForReview',
  'complete',
] as const;
export type AreaStatus = (typeof AREA_STATUSES)[number];

/** What this product may write about an area. Upstream, `complete` and
 *  `readyForReview` are each a toggle; `planned` clears whichever is set. */
export const WRITABLE_AREA_STATUSES = ['complete', 'readyForReview', 'planned'] as const;
export type WritableAreaStatus = (typeof WRITABLE_AREA_STATUSES)[number];

/** What a user may report on areas: a manager (`tasks.execute`) reports them
 *  done and reviews submissions; a Limited user (`tasks.contribute`) submits
 *  them for review; anyone else only reads. */
export type AreaReportTier = 'execute' | 'contribute' | null;

export function areaReportTier(canExecute: boolean, canContribute: boolean): AreaReportTier {
  if (canExecute) return 'execute';
  return canContribute ? 'contribute' : null;
}

/** What a tap on an area does: write a status, or — a manager on an area
 *  awaiting review — open the review. */
export type AreaAction = WritableAreaStatus | 'review';

/**
 * What a tap on an area does for a user of `tier` (ruling 3); `null` — nothing,
 * the area is a plain cell.
 *
 * - `execute`: an area under way is reported **done**; one awaiting review
 *   opens the **review** (approve / reject); a done one is taken back.
 * - `contribute`: an area under way is **submitted for review**, and a
 *   submission is taken back; a done area is theirs no longer.
 * - Nobody reports on an `unplanned` area.
 */
export function nextAreaAction(status: AreaStatus, tier: AreaReportTier): AreaAction | null {
  if (tier === null || status === 'unplanned') return null;
  if (status === 'readyForReview') return tier === 'execute' ? 'review' : 'planned';
  if (status === 'complete') return tier === 'execute' ? 'planned' : null;
  return tier === 'execute' ? 'complete' : 'readyForReview';
}

/**
 * Whether a contributor may write `to` onto an area Trusstor holds as `from`
 * — the server's side of {@link nextAreaAction}. It asks only where the area
 * is and where it goes, not whether it moves: Trusstor's two toggles are no-ops
 * on an area already there, so a contributor's double tap (the client keeps
 * only the last of a quick submit-and-undo) is a harmless repeat, not a 403.
 */
export function contributorMayReport(from: AreaStatus, to: WritableAreaStatus): boolean {
  return to !== 'complete' && nextAreaAction(from, 'contribute') !== null;
}
export * from './progress.js';
